<template>
  <span ref="root" class="star-counter" :class="`star-counter--${tone}`">
    <StarIcon class="star-counter__icon" :size="iconSize" :filled="filled" />
    <span class="star-counter__value">{{ displayed }}</span>

    <!-- one "+1 ☆" per star, rising out of the bar and fading (design reference:
         shop-sterren-animatie.mp4 — ~20px up over ~0.45s, under the counter) -->
    <span class="star-counter__gains" aria-hidden="true">
      <span v-for="id in gains" :key="id" class="star-counter__gain">
        +1
        <StarIcon :size="Math.round(iconSize * 0.78)" :filled="false" />
      </span>
    </span>
  </span>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import StarIcon from './StarIcon.vue'

const props = defineProps({
  count: { type: Number, default: 0 },
  tone: { type: String, default: 'dark' }, // dark (on light bar) | light (on gradient)
  iconSize: { type: [Number, String], default: 22 },
  filled: { type: Boolean, default: true }, // top-bar counters use an outline star (Figma)
  // Count earned stars up one at a time instead of snapping to the new total.
  animate: { type: Boolean, default: false },
  // Stars that were earned while this counter was off screen (during a minigame):
  // the display starts that many below `count` so they can still be counted up.
  pending: { type: Number, default: 0 },
})
const emit = defineEmits(['celebrated'])

// One step per star, unhurried on purpose: at 280ms it read as a number ticking
// over rather than as stars arriving. Half a second apart lands mentally, and the
// chip still outlives its step so consecutive stars overlap.
const STEP_MS = 520
const GAIN_MS = 1150
const BEAT_MS = 620
// the beat is driven imperatively: at 280ms apart the beats overlap, and a CSS
// class cannot be re-triggered inside one tick — Element.animate always restarts
const BEAT = [
  { transform: 'scale(1)' },
  { transform: 'scale(1.5)', offset: 0.32 },
  { transform: 'scale(0.94)', offset: 0.62 },
  { transform: 'scale(1.06)', offset: 0.82 },
  { transform: 'scale(1)' },
]

const displayed = ref(props.count)
const gains = ref([])
const root = ref(null)

let gainId = 0
const timers = new Set()
function later(fn, ms) {
  const t = setTimeout(() => {
    timers.delete(t)
    fn()
  }, ms)
  timers.add(t)
  return t
}
function clearTimers() {
  for (const t of timers) clearTimeout(t)
  timers.clear()
}

const reducedMotion =
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** One tick: the number goes up by one, the star beats, a "+1" floats off. */
function tick() {
  displayed.value += 1
  for (const el of root.value?.querySelectorAll('.star-counter__icon, .star-counter__value') ?? [])
    el.animate?.(BEAT, { duration: BEAT_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' })

  const id = ++gainId
  gains.value.push(id)
  later(() => (gains.value = gains.value.filter((g) => g !== id)), GAIN_MS)
}

/** Walks `displayed` up to `count`, one star per step. */
function countUp() {
  clearTimers()
  const steps = props.count - displayed.value
  for (let i = 0; i < steps; i += 1) later(tick, i * STEP_MS)
  later(() => emit('celebrated'), Math.max(0, steps - 1) * STEP_MS)
}

function settle() {
  clearTimers()
  gains.value = []
  displayed.value = props.count
  emit('celebrated')
}

// Stars earned off screen: rewind the display, then count them up on arrival.
if (props.animate && props.pending > 0 && !reducedMotion) {
  displayed.value = Math.max(0, props.count - props.pending)
  countUp()
} else if (props.pending > 0) {
  emit('celebrated')
}

watch(
  () => props.count,
  (next, prev) => {
    // Only earning is celebrated; spending (the shop's stepper) tracks instantly.
    if (!props.animate || reducedMotion || next <= prev) return settle()
    countUp()
  },
)

onBeforeUnmount(clearTimers)
</script>

<style lang="scss" scoped>
.star-counter {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px; // Figma 443:375 / 426:256
  font-family: var(--font-brand);
  font-weight: var(--fw-bold);
  font-size: var(--fs-lg); // Figma 18px
  // Figma gives the count a 23px line box next to the 24px star slot. The
  // default (1.4) line box is taller, which pulls the two apart optically.
  line-height: 23px;

  &--dark {
    color: var(--c-black);
  }
  &--light {
    color: var(--c-white);
  }
}

// the heartbeat runs on the star AND the number, so the pair reads as one badge
.star-counter__icon,
.star-counter__value {
  transform-origin: center;
}

// The chips hang below the counter and are allowed to escape the bar; the bar
// itself carries the z-index that keeps them above the screen behind it.
.star-counter__gains {
  position: absolute;
  top: 100%;
  left: 50%;
  width: 0;
  height: 0;
}
.star-counter__gain {
  position: absolute;
  // Straight off the reference clip (shop-sterren-animatie.mp4, a 390-wide
  // capture so its pixels are ours): the chip sits ~44px below the bar's
  // bottom edge and rises ~17px, well clear of the white bar the whole way.
  top: 44px;
  left: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-xl); // bigger than the counter it feeds — this is the event
  color: var(--c-white);
  white-space: nowrap;
  transform: translateX(-50%);
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4); // readable on any panel below
  animation: star-gain 1.15s var(--ease-out) forwards;
  pointer-events: none;
}

@keyframes star-gain {
  from {
    opacity: 0;
    transform: translate(-50%, 10px) scale(0.6);
  }
  18% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1.08);
  }
  30% {
    transform: translate(-50%, -3px) scale(1);
  }
  65% {
    opacity: 1;
    transform: translate(-50%, -14px) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -26px) scale(0.98);
  }
}
</style>
